import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Download, UserPlus, KeyRound } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle } from "@/components/admin/AdminLayout";

const TRIGGERS = [["registered", "Spedizione registrata"], ["taken", "Presa in carico"], ["departed", "Partita dal centro logistico"], ["out_for_delivery", "In consegna"], ["delivered", "Consegnata"], ["delay", "Ritardo (tentativo non riuscito, riprogrammata, giacenza)"]];
const CHANNELS = [["email", "Email", "Attivo tramite il servizio email integrato."], ["whatsapp", "WhatsApp Business API", "Richiede token e numero WhatsApp Business approvati (WHATSAPP_API_TOKEN, WHATSAPP_PHONE_NUMBER_ID)."], ["sms", "SMS", "Richiede le credenziali di un provider SMS (SMS_PROVIDER_API_KEY, SMS_SENDER_ID)."]];
const Box = ({ title, children }) => <div className="bg-white border border-slate-200 p-5 sm:p-6"><h2 className="font-display text-lg font-bold uppercase mb-4">{title}</h2>{children}</div>;

const DelaySettings = () => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin/delay-check").then((r) => setC(r.data.config)); }, []);
  if (!c) return <Loader2 className="h-5 w-5 animate-spin" />;
  const save = async (next) => {
    setC(next);
    try {
      const { data } = await api.put("/admin/settings/delay-check", next);
      setC(data);
      toast.success("Impostazioni del controllo ritardi salvate");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Box title="Controllo ritardi giornaliero">
      <div className="flex items-center justify-between gap-4 py-2.5 border-b border-slate-100">
        <div><p className="text-sm font-semibold">Orario del controllo</p><p className="text-xs text-slate-500">Ora italiana. Il riepilogo arriva alla tua email e i clienti vengono avvisati a quest'ora.</p></div>
        <Select value={String(c.hour)} onValueChange={(v) => save({ ...c, hour: Number(v) })}>
          <SelectTrigger className="w-28 rounded-none" data-testid="delay-hour-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)} data-testid={`delay-hour-option-${h}`}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between gap-4 py-2.5">
        <div><p className="text-sm font-semibold">Riepilogo anche senza ritardi</p><p className="text-xs text-slate-500">Ricevi ogni giorno un'email di conferma anche quando non ci sono spedizioni in ritardo.</p></div>
        <Switch checked={c.summary_when_none} onCheckedChange={(v) => save({ ...c, summary_when_none: v })} data-testid="delay-summary-none-switch" />
      </div>
    </Box>
  );
};

const Notifications = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings/notifications").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin" />;
  const save = async (next) => {
    setS(next);
    try {
      const { data } = await api.put("/admin/settings/notifications", { email_enabled: next.email_enabled, whatsapp_enabled: next.whatsapp_enabled, sms_enabled: next.sms_enabled, triggers: next.triggers });
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6">
      <Box title="Canali">
        <div className="divide-y divide-slate-100">
          {CHANNELS.map(([k, label, help]) => (
            <div key={k} className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="font-semibold flex items-center gap-2">{label}
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${s.configured[k] ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid={`channel-${k}-status`}>{s.configured[k] ? "Configurato" : "Da configurare"}</span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{help}</p>
              </div>
              <Switch checked={s[`${k}_enabled`]} onCheckedChange={(v) => save({ ...s, [`${k}_enabled`]: v })} data-testid={`channel-${k}-switch`} />
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">Le notifiche automatiche vengono inviate solo se il destinatario ha dato il consenso (casella nella scheda spedizione). I canali non configurati vengono saltati e registrati nel log.</p>
      </Box>
      <DelaySettings />
      <Box title="Eventi che generano notifiche">
        {TRIGGERS.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
            <span className="text-sm">{label}</span>
            <Switch checked={s.triggers[k]} onCheckedChange={(v) => save({ ...s, triggers: { ...s.triggers, [k]: v } })} data-testid={`trigger-${k}-switch`} />
          </div>
        ))}
      </Box>
    </div>
  );
};

const Users = () => {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const load = () => api.get("/admin/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", f); toast.success("Amministratore creato"); setF({ name: "", email: "", password: "" }); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Revocare l'accesso a ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Accesso revocato"); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-6">
      <Box title="Amministratori autorizzati">
        {!list ? <Loader2 className="h-5 w-5 animate-spin" /> : list.map((u) => (
          <div key={u.id} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0" data-testid={`user-row-${u.email}`}>
            <div><p className="font-semibold text-sm">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></div>
            {u.id !== user.id && <Button size="sm" variant="outline" className="rounded-none text-red-600" onClick={() => remove(u)} data-testid={`user-delete-${u.email}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </Box>
      <Box title="Aggiungi amministratore">
        <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input required placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none" data-testid="new-user-name" />
          <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none" data-testid="new-user-email" />
          <Input required type="password" minLength={10} placeholder="Password (min. 10 caratteri)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-none" data-testid="new-user-password" />
          <Button type="submit" className="rounded-none bg-slate-900 sm:col-span-3 sm:w-fit" data-testid="new-user-submit"><UserPlus className="h-4 w-4 mr-1" /> Crea accesso</Button>
        </form>
      </Box>
    </div>
  );
};

const Security = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", f); toast.success("Password aggiornata"); setF({ current_password: "", new_password: "" }); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-6">
      <Box title="Cambia password">
        <form onSubmit={submit} className="space-y-3 max-w-sm">
          <div><Label>Password attuale</Label><Input required type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className="mt-1 rounded-none" data-testid="current-password-input" /></div>
          <div><Label>Nuova password (min. 10 caratteri)</Label><Input required minLength={10} type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="mt-1 rounded-none" data-testid="new-password-input" /></div>
          <Button type="submit" className="rounded-none bg-slate-900" data-testid="change-password-btn"><KeyRound className="h-4 w-4 mr-1" /> Aggiorna password</Button>
        </form>
      </Box>
      <Box title="Backup del database">
        <p className="text-sm text-slate-600 mb-4">Scarica una copia completa di spedizioni, eventi, servizi, messaggi e registri in formato JSON (le password non sono incluse). Conserva il file in un luogo sicuro.</p>
        <Button variant="outline" className="rounded-none" onClick={() => downloadFile("/admin/backup", {}, "backup.json").catch(() => toast.error("Backup non riuscito"))} data-testid="backup-download-btn"><Download className="h-4 w-4 mr-1" /> Scarica backup</Button>
      </Box>
    </div>
  );
};

export default function Settings() {
  return (
    <div className="max-w-3xl" data-testid="admin-settings">
      <PageTitle title="Impostazioni" />
      <Tabs defaultValue="notifications">
        <TabsList className="rounded-none bg-slate-200/70">
          <TabsTrigger value="notifications" className="rounded-none" data-testid="settings-tab-notifications">Notifiche</TabsTrigger>
          <TabsTrigger value="users" className="rounded-none" data-testid="settings-tab-users">Amministratori</TabsTrigger>
          <TabsTrigger value="security" className="rounded-none" data-testid="settings-tab-security">Sicurezza e backup</TabsTrigger>
        </TabsList>
        <TabsContent value="notifications" className="mt-4"><Notifications /></TabsContent>
        <TabsContent value="users" className="mt-4"><Users /></TabsContent>
        <TabsContent value="security" className="mt-4"><Security /></TabsContent>
      </Tabs>
    </div>
  );
}
